/* © CNES */
import Keycloak from 'keycloak-js';
import { AuthenticationResolution } from '../../models/AuthenticationResolution';
import {
  AuthenticatedResult,
  NotAuthenticatedResult,
  notAuthenticatedStatus,
  SessionToken,
} from '../../models/AuthenticationStatus';
import { apiConfiguration } from '../APIConfiguration';

/**
 * Authentication API management, based on keycloak-js
 */
export const keycloakAdapter = new Keycloak({
  clientId: apiConfiguration.authentication.client,
  realm: apiConfiguration.authentication.realm,
  url: apiConfiguration.authentication.url,
});

/**
 *  get session token from keycloakAdapter.
 * @returns SessionToken: the session token
 */
const getSessionToken = (): SessionToken | null => {
  const { token, tokenParsed } = keycloakAdapter;
  return token && tokenParsed?.exp
    ? {
        value: token,
        // exp stands for expiration date, it is always in token, by token configuration of our Keycloak instance.
        // exp is expressed as a shift from 1970, BUT IN SECONDS (hence the 1000 factor to get the shift)
        expiresInMS: tokenParsed.exp! * 1000 - Date.now(),
      }
    : null;
};

/**
 * authentication API to make available methods from keycloack (init, login, logout, etc)
 */
export const authenticationAPI = {
  init: async (): Promise<NotAuthenticatedResult | AuthenticatedResult> => {
    try {
      await keycloakAdapter.init({
        responseMode: 'fragment',
        flow: 'standard',
        pkceMethod: 'S256', // standard flow + PKCE
        onLoad: 'check-sso',
        checkLoginIframe: false,
      });

      if (
        keycloakAdapter.authenticated &&
        keycloakAdapter.idToken &&
        keycloakAdapter.idTokenParsed &&
        keycloakAdapter.token &&
        keycloakAdapter.tokenParsed
      ) {
        return {
          status: AuthenticationResolution.authenticated,
          login: {
            name: keycloakAdapter.idTokenParsed.name,
            email: keycloakAdapter.idTokenParsed.email,
            token: getSessionToken()!,
          },
        };
      }
      return notAuthenticatedStatus;
    } catch (e) {
      return notAuthenticatedStatus;
    }
  },
  login: keycloakAdapter.login.bind(keycloakAdapter),
  logout: keycloakAdapter.logout.bind(keycloakAdapter),
  getAuthorizationHeaders: (): Record<string, string> =>
    keycloakAdapter.authenticated && keycloakAdapter.token
      ? {
          Authorization: `Bearer ${keycloakAdapter.token}`,
        }
      : {},
  refreshSessionToken: async (): Promise<SessionToken | null> => {
    try {
      const success = await keycloakAdapter.updateToken(
        Number.MAX_SAFE_INTEGER
      );
      if (success) {
        return getSessionToken();
      }
    } catch {
      // common exit
    }
    return null;
  },
};
